"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

export const pointsHistoryInputs = [
  {
    name: "title",
    type: "string",
    required: true,
    defaultValue: "Points history",
  },
  {
    name: "dateLabel",
    type: "string",
    required: true,
    friendlyName: "Label for table's date column",
    defaultValue: "Date",
  },
  {
    name: "eventLabel",
    type: "string",
    required: true,
    friendlyName: "Label for table's event column",
    defaultValue: "Event",
  },
  {
    name: "pointsLabel",
    type: "string",
    required: true,
    friendlyName: "Label for table's points column",
    defaultValue: "Points",
  },
  {
    name: "balanceLabel",
    type: "string",
    required: true,
    friendlyName: "Label for table's balance column",
    defaultValue: "Balance",
  },
  {
    name: "loadAmount",
    type: "number",
    required: true,
    friendlyName: "Amount of items shown on initial load",
    defaultValue: 5,
  },
  {
    name: "isFullPointsHistory",
    type: "boolean",
    required: true,
    friendlyName: "Show full points history",
    defaultValue: false,
    helperText: "Toggle to show the full points history or a summarized view.",
  },
  {
    name: "text",
    type: "string",
    required: true,
    showIf: 'options.get("isFullPointsHistory") === false',
    friendlyName: "Link's label",
    defaultValue: "View full points history",
  },
  {
    name: "url",
    type: "url",
    required: true,
    showIf: 'options.get("isFullPointsHistory") === false',
    friendlyName: "Link's URL",
    defaultValue: "#",
  },
  {
    name: "defaultPointsAddedMessage",
    type: "string",
    required: true,
    friendlyName: "Default message shown when points are added.",
    defaultValue: "Points added",
  },
  {
    name: "defaultPointsSpentMessage",
    type: "string",
    required: true,
    friendlyName: "Default message shown when points are spent.",
    defaultValue: "Points spent",
  },
  {
    name: "pointsRefundMessage",
    type: "string",
    required: false,
    friendlyName: "Message shown when points are refunded.",
    defaultValue: "Points refunded",
  },
  {
    name: "pointsRedemptionMessage",
    type: "string",
    required: true,
    friendlyName: "Message shown when points are redeemed.",
    defaultValue: "Points redemption - {#event#}",
    helperText: "Supports usage of placeholders: {#event#}",
  },
  {
    name: "pointsCouponRedemptionMessage",
    type: "string",
    required: true,
    friendlyName: "Message shown when points are redeemed as a coupon.",
    defaultValue: "{#event#} - coupon: {#coupon#}",
    helperText: "Supports usage of placeholders: {#event#}, {#coupon#}",
  },
  {
    name: "pointsOrderAddedMessage",
    type: "string",
    required: true,
    friendlyName: "Message shown when points are added for an order.",
    defaultValue: "{#event#} - order {#orderUrl#}",
    helperText: "Supports usage of placeholders: {#event#}, {#coupon#}",
  },
  {
    name: "pointsCancellationMessage",
    type: "string",
    required: true,
    friendlyName: "Message shown when points are canceled.",
    defaultValue: "Points canceled",
  },
  {
    name: "pointsExpirationMessage",
    type: "string",
    required: true,
    friendlyName: "Message shown when points are expired.",
    defaultValue: "Points expired",
  },
  {
    name: "externalCouponLinkLabel",
    type: "string",
    required: false,
    friendlyName: "Label for an external link to a coupon redemption.",
    defaultValue: "Redeem coupon",
  },
  {
    name: "redemptionFailedMessage",
    type: "string",
    required: false,
    friendlyName: "Message shown when prize redemption has permanently failed.",
    defaultValue: "Prize redemption failed",
  },
  {
    name: "isCouponDisplayedInNewLineOnMobile",
    type: "boolean",
    required: false,
    friendlyName: "Display coupon in new line",
    defaultValue: false,
  },
  {
    name: "pinLabel",
    type: "string",
    required: false,
    friendlyName: "Label for PIN",
    showIf: `options.get("isCouponDisplayedInNewLineOnMobile") === true`,
  },
  {
    name: "couponLabel",
    type: "string",
    required: false,
    friendlyName: "Label for COUPON",
    showIf: `options.get("isCouponDisplayedInNewLineOnMobile") === true`,
  },
] as const satisfies Inputs;

export const PointsHistoryComponent: RegisteredComponent = {
  name: "PointsHistory",
  component: dynamic(async () =>
    withValidation(pointsHistoryInputs, (await import("./PointsHistory")).PointsHistory, "PointsHistory")
  ),
  inputs: pointsHistoryInputs,
  requiresParent: {
    query: {
      "component.name": { $in: ["LoyaltyWrapper"] },
    },
    message: "'PointsHistory' component can only be added inside a 'LoyaltyWrapper'",
  },
};
